import axios, { AxiosError } from 'axios';

const BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:3000';

export const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// When a request body is FormData, drop the instance-level
// Content-Type so the browser sets the correct multipart boundary.
// Without this, file uploads (meishi, contact CSV bulk-import) silently
// post as application/json and the backend sees no file → 400.
api.interceptors.request.use((cfg) => {
  if (typeof FormData !== 'undefined' && cfg.data instanceof FormData) {
    if (cfg.headers && 'Content-Type' in cfg.headers) {
      delete (cfg.headers as Record<string, unknown>)['Content-Type'];
    }
  }
  return cfg;
});

export interface GeoHint {
  country: string | null;
  city: string | null;
  source: 'cloudflare' | 'none';
}

export async function getGeoHint(): Promise<GeoHint> {
  const r = await api.get<GeoHint>('/api/geo/hint');
  return r.data;
}

/* ── Global 403 plan_required handler ─────────────────────────────────────
 * The backend's @RequiresPlan guard returns
 *   { error: 'plan_required', required: 'growth' | 'global' | 'enterprise', actual: ... }
 * Pages that haven't been wrapped with a client-side lock yet still need to
 * tell the user *why* the request was rejected — without this, they'd see
 * a silent failure. We dispatch a `patronez:plan-required` event on the
 * window; the ToastProvider listens and shows the upgrade toast.
 */
export interface PlanRequiredEventDetail {
  required: 'growth' | 'global' | 'enterprise';
  actual: 'free' | 'growth' | 'global' | 'enterprise';
}

interface PlanRequiredBody {
  error: 'plan_required';
  required: PlanRequiredEventDetail['required'];
  actual: PlanRequiredEventDetail['actual'];
}

function isPlanRequiredBody(b: unknown): b is PlanRequiredBody {
  return (
    !!b &&
    typeof b === 'object' &&
    (b as { error?: unknown }).error === 'plan_required' &&
    typeof (b as { required?: unknown }).required === 'string'
  );
}

api.interceptors.response.use(
  (r) => r,
  (err: AxiosError) => {
    if (err.response?.status === 403 && isPlanRequiredBody(err.response.data)) {
      const detail: PlanRequiredEventDetail = {
        required: err.response.data.required,
        actual: err.response.data.actual,
      };
      // Dispatch on next tick so React state updates from the originating
      // call settle before the toast pops — avoids flicker-then-flicker.
      if (typeof window !== 'undefined') {
        window.setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent<PlanRequiredEventDetail>('patronez:plan-required', {
              detail,
            }),
          );
        }, 0);
      }
    }
    return Promise.reject(err);
  },
);
